import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect
} from "react";
import { Portal } from "react-portal";
import styled from "styled-components";
import { SubtitleTxt } from "storybook-components/Typography";
import CloseIconSvg from "./CloseIconSvg";

const ModalBackdrop = styled.div`
  display: flex;
  position: fixed;
  top: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
`;

const ModalContainer = styled.div`
  background-color: white;
  opacity: 1;
  margin: auto;
  max-width: 640px;
`;

const CloseButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray400};
  padding: 8px;
`;

const BodySection = styled.div`
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  background-color: white;
  outline: none;
  border: none;
  cursor: pointer;
  &:hover {
    svg {
      .change-on-hover {
        fill: ${({ theme }) => theme.colors.black};
      }
      .changeFillOnlyOnHover {
        stroke: none;
        fill: ${({ theme }) => theme.colors.white};
      }
    }
    div {
      border-color: ${({ theme }) => theme.colors.gray1100};
    }
  }
`;

const StyledSubtitleTxt = styled(SubtitleTxt)`
  color: ${({ theme }) => theme.colors.gray1200};
`;

/*
  Basic modal. 
  Opened via useImperativeHandle and a ref implemented by the parent Component. 
  openModal() and closeModal() are made available via a ref. 
  Example:
  
  import React, { useRef } from "react";
  import Modal from "./Modal";
  export default ModalLaunchingBtn = () => {
    const modalRef = useRef();
    return (
      <div>
        <button onClick={() => modalRef.current.openModal()}>
          Hi I open a modal
        </button>
        <Modal ref={modalRef}>
          <p>Hi I'm a modal nice to meet you.</p>
        </Modal>
      </div>
    );
  };
*/

const Modal = forwardRef((props, ref) => {
  const { children, title } = props;
  const modalRef = useRef(null);
  const [display, setDisplay] = useState(false);

  const handleOpen = () => {
    setDisplay(true);
  };

  const handleClose = () => {
    setDisplay(false);
  };

  useImperativeHandle(ref, () => ({
    openModal: () => handleOpen(),
    closeModal: () => handleClose()
  }));

  function handleBackgroundClicks(handler) {
    // Bind the event listener
    const setup = () => document.addEventListener("mouseup", handler);
    // Unbind the event listener on clean up
    const teardown = () => document.removeEventListener("mouseup", handler);

    useEffect(() => {
      setup();
      return teardown;
    });
  }

  handleBackgroundClicks(event => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      handleClose();
    }
  });

  if (display) {
    return (
      <Portal>
        <ModalBackdrop>
          <ModalContainer ref={modalRef}>
            <CloseButtonRow>
              <div />
              <StyledSubtitleTxt>{title}</StyledSubtitleTxt>
              <CloseButton type="close" onClick={handleClose}>
                <CloseIconSvg />
              </CloseButton>
            </CloseButtonRow>
            <BodySection>{children}</BodySection>
          </ModalContainer>
        </ModalBackdrop>
      </Portal>
    );
  }
  return null;
});

Modal.defaultProps = {
  title: ""
};

export default Modal;
